import { useState } from 'react';
import SectionLabel from '../ui/SectionLabel';
import SectionTitle from '../ui/SectionTitle';
import BtnPrimary from '../ui/BtnPrimary';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { useAppContext } from '../../context/AppContext';
import { contact } from '../../services/contactService';
import { LandingTrans } from '../../utils/translations';

const LOCATION_IDS = [
  { id: 'studio', embedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3380.2619922693457!2d34.81327677507074!3d32.08920417395873!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x151d4b96ba065fbd%3A0x47802fb082d428ed!2sYoga%20Studio%20-%20Once%20Up%20On%20a%20Mat!5e0!3m2!1sen!2sil!4v1773519308466!5m2!1sen!2sil' },
  { id: 'park', embedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3381.6823461754466!2d34.8274635!3d32.050788499999996!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x151d4b7a74c128dd%3A0x2c8ed136233cb4bf!2z157Xqten16DXmSDXm9eV16nXqA!5e0!3m2!1sen!2sil!4v1773519367205!5m2!1sen!2sil' },
];

export default function ContactSection({ onFormSuccess }) {
  const { language, setToaster } = useAppContext();
  const t = LandingTrans[language];
  const [ref, visible] = useScrollReveal();
  const [formData, setFormData] = useState({ name: '', phone: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeMap, setActiveMap] = useState('studio');

  const LOCATIONS = LOCATION_IDS.map((loc) => ({
    ...loc,
    name: loc.id === 'studio' ? t.locationStudio : t.locationPark,
  }));
  const contactItems = [
    { labelKey: 'addressLabel', value: t.address, sub: t.addressSub, link: null },
    { labelKey: 'whatsappLabel', value: '+972 55 911 69 75', link: 'https://wa.me/972559116975' },
    { labelKey: 'emailLabel', value: 'anna.joy.inbox@gmail.com', link: 'mailto:anna.joy.inbox@gmail.com' },
    { labelKey: 'instagramLabel', value: '@joykayoga', link: 'https://www.instagram.com/joykayoga/' },
  ].map((item) => ({ ...item, label: t[item.labelKey] }));
  const activeLocation = LOCATIONS.find((l) => l.id === activeMap) ?? LOCATIONS[0];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await contact(formData);
      setFormData({ name: '', phone: '', message: '' });
      setSubmitted(true);
      setToaster({
        message: LandingTrans[language].formSuccess,
        type: 'success',
        show: true,
      });
      onFormSuccess?.();
    } catch (error) {
      console.error(error);
      setToaster({
        message: LandingTrans[language].formError,
        type: 'error',
        show: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      ref={ref}
      className={`bg-cream py-16 md:py-30 px-6 md:px-20 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
    >
      {/* Mobile: stacked (contacts then map). Desktop: 2 columns side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-12 max-w-6xl mx-auto">
        <div className="min-w-0">
          <SectionLabel>{t.contactLabel}</SectionLabel>
          <SectionTitle className="mt-4 mb-10">
            {t.contactTitle}<em className="italic text-clay">{t.contactTitleEm}</em>
          </SectionTitle>
          <div className="space-y-5">
            {contactItems.map((item) => (
              <div key={item.labelKey} className="block">
                <span className="block text-left text-[11px] font-sans font-medium uppercase tracking-wider text-clay mb-1">
                  {item.label}
                </span>
                <div className="text-left">
                  {item.link ? (
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[15px] font-sans font-light text-ink-mid border-b border-sand-dark hover:border-clay hover:text-clay"
                    >
                      {item.value}
                    </a>
                  ) : (
                    <span className="text-[15px] font-sans font-light text-ink-mid">{item.value}</span>
                  )}
                  {item.sub && (
                    <p className="text-[13px] font-sans text-ink-light mt-1">{item.sub}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 md:flex md:justify-start">
            <BtnPrimary href="/schedule">
              {t.signUpClass}
            </BtnPrimary>
          </div>
        </div>

        {/* Map: below contacts on mobile; second column on desktop */}
        <div className="min-w-0 mt-10 md:mt-0 w-full">
          <div className="flex gap-2 mb-4 w-full">
            {LOCATIONS.map((loc) => (
              <button
                key={loc.id}
                type="button"
                onClick={() => setActiveMap(loc.id)}
                className={`px-4 py-2 rounded-sm text-[13px] font-sans font-medium transition-colors ${activeMap === loc.id
                  ? 'bg-clay text-white'
                  : 'bg-white border border-sand-dark text-ink-mid hover:border-clay hover:text-clay'
                  }`}
              >
                {loc.id === 'studio' ? t.studioBtn : t.parkBtn}
              </button>
            ))}
          </div>
          <div className="w-full aspect-[4/3] rounded-lg overflow-hidden border border-sand-dark bg-sand-dark">
            <iframe
              key={activeMap}
              src={activeLocation.embedUrl}
              width="100%"
              height="100%"
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title={activeLocation.name}
              className="w-full h-full"
            />
          </div>
        </div>
      </div>

        {/* <div>
          <p className="text-sm font-sans font-light text-ink-mid mb-6">
            Оставьте заявку и я свяжусь с вами в ближайшее время.
          </p>
          {submitted ? (
            <p className="text-clay font-sans font-medium">Заявка отправлена! Скоро свяжемся.</p>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div>
                <label className="block text-[11px] font-sans font-medium uppercase tracking-wider text-ink-light mb-1.5">
                  {LandingTrans[language].yourName}
                </label>
                <input
                  type="text"
                  name="name"
                  placeholder={LandingTrans[language].yourName}
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full font-sans text-[15px] font-light text-ink bg-white border border-sand-dark rounded-sm px-4 py-3.5 focus:border-clay focus:outline-none transition-colors"
                  required
                />
              </div>
              <div>
                <label className="block text-[11px] font-sans font-medium uppercase tracking-wider text-ink-light mb-1.5">
                  {LandingTrans[language].yourPhone}
                </label>
                <input
                  type="tel"
                  name="phone"
                  placeholder={LandingTrans[language].yourPhone}
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full font-sans text-[15px] font-light text-ink bg-white border border-sand-dark rounded-sm px-4 py-3.5 focus:border-clay focus:outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-[11px] font-sans font-medium uppercase tracking-wider text-ink-light mb-1.5">
                  {LandingTrans[language].commentOrQuestion}
                </label>
                <textarea
                  name="message"
                  placeholder={LandingTrans[language].commentOrQuestion}
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  className="w-full font-sans text-[15px] font-light text-ink bg-white border border-sand-dark rounded-sm px-4 py-3.5 focus:border-clay focus:outline-none transition-colors resize-y min-h-24"
                  required
                />
              </div>
              <BtnPrimary type="submit" className="self-start flex items-center justify-center gap-2" disabled={loading}>
                {loading ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      aria-hidden
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8z"
                      />
                    </svg>
                    {LandingTrans[language].sending}
                  </>
                ) : (
                  LandingTrans[language].send
                )}
              </BtnPrimary>
            </form>
          )}
        </div> */}
    </section>
  );
}
